//@author: devin
import { BrowserModule } from "@angular/platform-browser";
import { HttpClient, HttpXhrBackend, provideHttpClient, withXhr } from "@angular/common/http";
import { APP_INITIALIZER, ErrorHandler, inject, Injector, NgModule, NgZone, DoBootstrap } from "@angular/core";
import { S25AiSearchChatbotComponent } from "../modules/s25-chat-bot/s25.search.chat.bot.component";
import { S25HelpModule } from "../modules/s25-help/s25.help.module";
import { S25IconComponent } from "../modules/s25-icon/s25.icon.component";
import { S25HomeSearchComponent } from "../modules/s25-ql/s25.home.search.component";
import { SaveSearchModalComponent } from "../modules/s25-ql/save.search.modal.component";
import { OrganizationExportModalComponent } from "../modules/student-engagement/export/organization.export.modal.component";
import { S25ModalShareSearchComponent } from "../modules/s25-ql/s25.modal.share.search.component";
import { CalendarWithOptions } from "../standalone/calendar/calendar.with.options.component";
import { ListWithOptions } from "../standalone/lists/list.with.options.component";
import { S25EventSummaryComponent } from "../standalone/s25-event-summary/s25.event.summary.component";
import { S25ItemNameComponent } from "../standalone/s25-item-name/s25.item.name.component";
import { S25ToggleButtonModule } from "../modules/s25-toggle-button/s25.toggle.button.module";
import { S25EditableBooleanModule } from "../modules/s25-editable/s25-editable-boolean/s25.editable.boolean.module";
import { S25MultiQueryModule } from "../modules/s25-multi-query/s25.multi.query.module";
import { S25AddressModule } from "../modules/s25-address/s25.address.module";
import { S25NumWeeksChooserModule } from "../modules/s25-num-weeks-chooser/s25.num.weeks.chooser.module";
import { S25ItemModule } from "../modules/s25-item/s25.item.module";
import { S25ItemComponent } from "../modules/s25-item/s25.item.component";
import { S25ItemUnwrapperComponent } from "../modules/s25-item/s25.item.unwrapper.component";
import { ServiceLoader } from "../services/service.loader";
import { ModalModule } from "../modules/modal/modal.module";
import { PopoverModule } from "../modules/s25-popover/popover.module";
import { AppComponent } from "./app.component";
import { CompileService } from "./compile.service";
import { S25ImageModule } from "../modules/s25-image/s25.image.module";
import { EmailDefaultsComponent } from "../modules/system-settings/email-defaults/email.defaults.component";
import { SystemDefaultsComponent } from "../modules/system-settings/system-defaults/system.defaults.component";
import { UserDefaultsComponent } from "../modules/system-settings/user-defaults/user.defaults.component";
import { EventSettingsComponent } from "../modules/system-settings/event-settings/event.settings.component";
import { LocationSettingsComponent } from "../modules/system-settings/location-settings/location.settings.component";
import { ResourceSettingsComponent } from "../modules/system-settings/resource-settings/resource.settings.component";
import { OrganizationSettingsComponent } from "../modules/system-settings/organization-settings/organization.settings.component";
import { TaskSettingsComponent } from "../modules/system-settings/task-settings/task.settings.component";
import { S25RichTextEditorModule } from "../modules/s25-rich-text-editor/s25.rich.text.editor.module";
import { S25BreakpointModule } from "../modules/s25-breakpoint/s25.breakpoint.module";
import { S25DatetimePrefsComponent } from "../modules/s25-datetime-prefs/s25.datetime.prefs.component";
import { S25TimepickerModule } from "../modules/s25-timepicker/s25.timepicker.module";
import { S25DatepickerModule } from "../modules/s25-datepicker/s25.datepicker.module";
import { S25LoadingInlineModule } from "../modules/s25-loading/s25.loading.inline.module";
import { S25DropdownPaginatedModule } from "../modules/s25-dropdown/s25.dropdown.paginated.module";
import { S25SimpleCollapseModule } from "../modules/s25-simple-collapse/s25.simple.collapse.module";
import { S25NotificationPolicyModule } from "../modules/s25-notification-policy/s25.notification.policy.module";
import { S25LynxModule } from "../modules/s25-lynx/s25.lynx.module";
import { S25EditableModule } from "../modules/s25-editable/s25.editable.module";
import { BpeModule } from "../modules/bpe/bpe.module";
import { EventStateDefaultComponent } from "../modules/system-settings/event-form-settings/event-state-default/event.state.default.component";
import { S25MultiselectModule } from "../modules/s25-multiselect/s25-multiselect.module";
import { S25BulkEditModule } from "../modules/s25-bulk-edit/s25.bulk.edit.module";
import { S25SecurityLinkModule } from "../modules/s25-security-link/s25.security.link.module";
import { S25PipesModule } from "../pipes/s25.pipes.module";
import { S25DeleteModule } from "../modules/s25-delete/s25.delete.module";
import { PublisherSettingsComponent } from "../modules/system-settings/publisher-settings/publisher.settings.component";
import { S25PricingModule } from "../modules/s25-pricing/s25.pricing.module";
import { S25ContactInfoModule } from "../modules/s25-contact-info/s25.contact.module";
import { S25EventTypeHierarchyModule } from "../modules/event-type-hierarchy/s25.event.type.hierarchy.module";
import { EventFormSettingsModule } from "../modules/system-settings/event-form-settings/event.form.settings.module";
import { S25ImageUploadModule } from "../modules/s25-image-upload/s25.image.upload.module";
import { S25ImageSelectorModule } from "../modules/s25-image-selector/s25.image.selector.module";
import { S25ImageFormModule } from "../modules/s25-image-form/s25.image.form.module";
import { S25ChangePasswordModule } from "../modules/s25-change-password/s25.change.password.module";
import { S25MasqueradeModule } from "../modules/S25-masquerade/masquerade.module";
import { S25DayChooserModule } from "../modules/s25-day-chooser/s25.day.chooser.module";
import { S25CancelRequestModule } from "../modules/s25-cancel-request/s25.cancel.request.module";
import { S25PreferenceToggleComponent } from "../modules/s25-preference-toggle/s25.preference.toggle.component";
import { S25RegisterButtonModule } from "../modules/s25-register-button/s25.register.button.module";
import { S25LocationAlwaysShareModule } from "../modules/s25-location-always-share/s25.location.always.share.module";
import { S25OptModule } from "../modules/s25-opt/s25.opt.module";
import { S25EventStatesModule } from "../modules/s25-event-states/s25.event.states.module";
import { S25ConflictsModule } from "../modules/s25-conflicts/s25.conflicts.module";
import { S25OfficeHoursSliderModule } from "../modules/s25-office-hours-slider/s25.office.hours.slider.module";
import { S25TaskModule } from "../modules/s25-task/s25.task.module";
import { S25ConstraintsModule } from "../modules/s25-constraints/s25.constraints.module";
import { S25ObjectModule } from "../modules/s25-object/s25.object.module";
import { S25DndSortableModule } from "../modules/s25-dnd-sortable/s25.dnd.sortable.module";
import { S25TableModule } from "../modules/s25-table/s25.table.module";
import { S25RuleTreeModule } from "../modules/s25-rule-tree/s25.rule.tree.module";
import { S25EventFormConfigsModule } from "../modules/system-settings/event-form-settings/s25-event-form-config/s25.event.form.configs.module";
import { S25EmailTemplateModule } from "../modules/system-settings/event-save-email/s25-email-template/s25.email.template.module";
import { S25InfoMessageModule } from "../modules/s25-info-message/s25.info.message.module";
import { S25MasterDefinitionsModule } from "../modules/master-definitions/s25.master.definitions.module";
import { S25EmailScenarioModule } from "../modules/system-settings/event-save-email/s25-email-scenario/s25.email.scenario.module";
import { S25RadioModule } from "../modules/s25-radio/s25.radio.module";
import { S25ChartsModule } from "../modules/s25-charts/s25.charts.module";
import { S25CustomAttributeModule } from "../modules/s25-custom-attribute/s25.custom.attribute.module";
import { S25TriggerRerenderModule } from "../modules/s25-trigger-rerender/s25.trigger.rerender.module";
import { S25WorkflowModule } from "../modules/system-settings/s25-workflow/s25.workflow.module";
import { S25DocumentManagementModule } from "../modules/system-settings/document-management/s25.document.management.module";
import { S25TaskViewModule } from "../modules/s25-task-view/s25.task.view.module";
import { S25EmbeddingModule } from "../modules/system-settings/embedding/s25-embedding/s25.embedding.module";
import { S25ModalModule } from "../modules/s25-modal/s25.modal.module";
import { S25CaptureEventModule } from "../modules/s25-capture-event/s25.capture.event.module";
import { S25PublisherModule } from "../modules/s25-publisher/s25.publisher.module";
import { S25PillModule } from "../modules/s25-pill/s25.pill.module";
import { S25BoardModule } from "../modules/s25-swarm-schedule/s25.board.module";
import { S25ProgressBarModule } from "../modules/s25-progress-bar/s25.progress.bar.module";
import { S25OptimizerEffectiveDatingModule } from "../modules/s25-optimizer-effective-dating/s25.optimizer.effective.dating.module";
import { S25ShiftSelectableModule } from "../modules/s25-shift-select/s25.shift.selectable.module";
import { S25BlackoutManagementModule } from "../modules/system-settings/blackout-management/s25.blackout.management.module";
import { S25SpecialDateManagementModule } from "../modules/system-settings/special-date-management/s25.special.date.management.module";
import { S25SpecialDateBulkUploadComponent } from "../modules/system-settings/special-date-management/s25.special.date.bulk.upload.component";
import { S25BuildingModule } from "../modules/s25-building/s25.building.module";
import { S25StandardScheduleModule } from "../modules/s25-standard-schedule/s25.standard.schedule.module";
import { S25DurationModule } from "../modules/s25-duration/s25.duration.module";
import { S25AvailabilityModule } from "../modules/s25-availability/s25.availability.module";
import { S25EventDetailsModule } from "../modules/s25-event/s25.event.details.module";
import { ContextMenuModule } from "../modules/s25-context-menu/context.menu.module";
import { S25MeetingPatternGridModule } from "../modules/s25-meeting-pattern-grid/s25.meeting.pattern.grid.module";
import { S25VirtualGridModule } from "../modules/s25-virtual-grid/s25.virtual.grid.module";
import * as Sentry from "@sentry/angular";
import { Router } from "@angular/router";
import { S25EventOccurrencesComponent } from "../modules/s25-event/s25-event-occurrences/s25.event.occurrences.component";
import { S25StripeSettingsModule } from "../modules/system-settings/stripe-settings/s25.stripe.settings.module";
import { S25ObjectAuditListModule } from "../modules/s25-object-audit-list/s25.object.audit.list.module";
import { S25ScheduledEmailsModule } from "../modules/s25-scheduled-emails/s25.scheduled.emails.module";
import { S25ScrollMinimapComponent } from "../standalone/s25-scroll-minimap/s25.scroll.minimap.component";
import { S25ButtonComponent } from "../standalone/s25.button.component";
import { S25ErrorsComponent } from "../modules/s25-errors/s25.errors.component";
import { S25SevenPointSolutionsSettingsModule } from "../modules/system-settings/seven-point-solutions-settings/s25.seven.point.solutions.settings.module";
import { S25DocusignSettingsComponent } from "../modules/system-settings/docusign-settings/s25.docusign.settings.component";
import { UserActivityModule } from "../modules/system-settings/user-activity/user.activity.module";
import { S25ReportFileTypeComponent } from "../standalone/s25.report.file.type.component";
import { S25ObjectSearchModule } from "../modules/s25-object-search/s25.object.search.module";
import { StudentHousingModule } from "../modules/student-housing/student.housing.module";
import { S25LinkParserComponent } from "../standalone/s25.link.parser.component";
import { S25ServicePortalModule } from "../modules/s25-service-portal/s25.service.portal.module";
import { S25AvailWeeklyComponent } from "../modules/s25-avail-weekly/s25.avail.weekly.component";
import { S25AvailLegendComponent } from "../modules/s25-avail-weekly/s25.avail.legend.component";
import { S25InlineDatepickerComponent } from "../standalone/s25.inline.datepicker.component";
import { S25MultiAvailWeeklyComponent } from "../modules/s25-avail-weekly/s25.multi.avail.weekly.component";
import { S25EmbeddedMultiAvailWeeklyComponent } from "../modules/s25-avail-weekly/s25.embedded.multi.avail.weekly.component";
import { SisImportExportSettingsComponent } from "../modules/system-settings/sis-import-export/sis.import.export.component";
import { EventFormModule } from "../modules/event-form/event.form.module";
import { EventFormProfileDropdownComponent } from "../modules/event-form/event.form.profile.dropdown.component";
import { MultiselectItemComponent } from "../modules/event-form/event.form.multiselect.item.component";
import { S25CopyVariableComponent } from "../modules/bpe/s25.copy.variable.component";
import { S25TabGroupComponent } from "../standalone/s25.tab.group.component";
import { S25ChatbotComponent } from "../modules/s25-chat-bot/s25.chat.bot.component";
import { S25ExpressSchedulingChatbotComponent } from "../modules/s25-chat-bot/s25.express.scheduling.chat.bot";
import { S25ExpressSchedulingComponent } from "../standalone/express-scheduling/s25.express.scheduling.component";
import { EvOccMappingComponent } from "../standalone/ev-occ-mapping.component";
import { S25ClickOutsideDirective } from "../modules/s25-click-outside/s25.click.outside.directive";
import { AdmitModule } from "../modules/admit/admit.module";
import { S25SidebarComponent } from "../modules/s25-sidebar/s25.sidebar.component";
import { S25FadingSuccessMsgComponent } from "../modules/s25-fading-success-msg/s25.fading.success.msg.component";
import { CalendaringModule } from "../modules/calendaring/calendaring.module";
import { S25NavBarComponent } from "../modules/s25-nav-bar/s25.nav.bar.component";
import { S25NotificationsModule } from "../modules/s25-notifications/s25.notifications.module";
import { S25ObjectDetailsModule } from "../modules/object-details/s25.object.details.module";
import { ImportModule } from "../modules/import/import.module";
import { S25DisplayFileComponent } from "../standalone/s25.display.file.component";
import { GoogleMapsModule } from "@angular/google-maps";
import { SeHomeComponent } from "../modules/student-engagement/se.home.component";
import { SeLoginComponent } from "../modules/student-engagement/se.login.component";
import { PasswordResetComponent } from "../standalone/password-reset/password.reset.component";
import { SeOrgHomeComponent } from "../modules/student-engagement/se.org.home.component";
import { MoreActionsCollapseComponent } from "../standalone/more-actions/more.actions.collapse.component";
import { DataIngestionModule } from "../modules/data-ingestion/data.ingestion.module";
import { ApprovalGroupsComponent } from "../modules/approval-groups/approval.groups.component";
import { MyWorkflowPageComponent } from "../modules/workflow/my-workflow/my.workflow.page.component";
import { WorkflowProcessesPageComponent } from "../modules/workflow/workflow.processes.page.component";
import { WorkflowProcessEditorComponent } from "../modules/workflow/workflow.process.editor.component";
import { WorkflowAddFormActionsModalComponent } from "../modules/workflow/modal/workflow.add.form.actions.modal.component";
import { WorkflowInitRulesComponent } from "../modules/workflow/workflow.init.rules.component";
import { WorkflowActionHistoryPageComponent } from "../modules/workflow/actions/workflow.action.history.page.component";
import { BudgetAdminComponent } from "../modules/student-engagement/budget/admin/budget.admin.component";
import { BudgetCategoryManagementComponent } from "../modules/student-engagement/budget/admin/budget.category.management.component";
import { FiscalYearManagementComponent } from "../modules/student-engagement/budget/admin/fiscal.year.management.component";
import { BudgetAdminRequestsComponent } from "../modules/student-engagement/budget/admin/budget.admin.requests.component";
import { BudgetReportsComponent } from "../modules/student-engagement/budget/reports/budget.reports.component";
import { S25ChatComponent } from "../standalone/s25.chat.component";
import { S25ChatPageComponent } from "../standalone/s25-chat-page/s25.chat.page.component";
import { S25ProductFeedbackComponent } from "../standalone/product-feedback/product.feedback.component";
import { SeConfigComponent } from "../modules/student-engagement/se.config.component";
import { SeConfigInitPageComponent } from "../modules/student-engagement/config/se.config.init.page.component";
import { SeConfigAppearancePageComponent } from "../modules/student-engagement/config/se.config.appearance.page.component";
import { SeConfigAuthenticationPageComponent } from "../modules/student-engagement/config/se.config.authentication.page.component";
import { SeConfigAlertsPageComponent } from "../modules/student-engagement/config/se.config.alerts.page.component";
import { SeEmailTemplateSettingsPageComponent } from "../modules/student-engagement/email/se.email.template.settings.page.component";
import { Connect25EmailTemplateAdminComponent } from "../modules/student-engagement/email/connect25.email.template.admin.component";
import { SeConfigImportPageComponent } from "../modules/student-engagement/config/se.config.import.page.component";
import { SeOrgStatusAdminComponent } from "../modules/student-engagement/se.org.status.admin.component";
import { SeSecurityComponent } from "../modules/student-engagement/securityConfig/se.security.config.component";
import { SeObjectSearchComponent } from "../modules/student-engagement/search/object-search/se.object.search.component";
import { SeUserSearchComponent } from "../modules/student-engagement/search/se.user.search.component";
import { SeServiceHoursComponent } from "../modules/student-engagement/service-hours/se.service.hours.component";
import { SeEventDetailsViewComponent } from "../modules/student-engagement/event/se.event.details.view.component";
import { ManageTaskComponent } from "../modules/s25-task/manage.tasks.component";
import { SeSidebarComponent } from "../modules/student-engagement/se.sidebar.component";
import { SeBreadcrumbComponent } from "../modules/student-engagement/se.breadcrumb.component";
import { Connect25TopBannerComponent } from "../modules/student-engagement/connect25.top.banner.component";
import { Connect25SystemAlertsComponent } from "../modules/student-engagement/c25-system-alerts/connect25.system.alerts.component";
import { SeSystemAlertsAdminComponent } from "../modules/student-engagement/se.system.alerts.admin.component";
import { SsoMappingsAdminComponent } from "../modules/sso-mappings/sso.mappings.admin.component";
import { ElectionBallotComponent } from "../modules/student-engagement/elections/election.ballot.component";
import { ElectionNominationsPageComponent } from "../modules/student-engagement/elections/election.nominations.page.component";
import { ElectionAdminPageComponent } from "../modules/student-engagement/elections/election.admin.page.component";
import { ElectionManualCandidatePageComponent } from "../modules/student-engagement/elections/election.manual.candidate.page.component";
import { ElectionResultsPageComponent } from "../modules/student-engagement/elections/election.results.page.component";
import { S25ReportOutputFileNameComponent } from "../standalone/s25.report.output.file.name.component";
import { EventSurveyComponent } from "../modules/data-ingestion/event.survey.component";
import { FormSubmissionComponent } from "../modules/data-ingestion/form.submission.component";
import { SeEventCheckinDashComponent } from "../modules/student-engagement/attendance/se.event.checkin.dash.component";
import { SeCheckInComponent } from "../modules/student-engagement/attendance/se.check.in.component";
import { LoginStore } from "../stores/login.store";
import { FlsStore } from "../stores/fls.store";
import { RecentlyViewedStore } from "../stores/recently.viewed.store";
import { RecentlyViewed } from "../standalone/recently-viewed/recently.viewed.component";
import { SeSecurityStore } from "../modules/student-engagement/se.security.store";
import { WaitlistComponent } from "../standalone/s25-waitlist/s25.waitlist.component";
import { S25UserProfileComponent } from "../modules/student-engagement/user.profile.component";
import { SeMyActivityComponent } from "../modules/student-engagement/engagement-metrics/se.my.activity.component";
import { SeEngagementDashboardComponent } from "../modules/student-engagement/engagement-metrics/se.engagement.dashboard.component";
import { SeAnalyticsDashboardComponent } from "../modules/student-engagement/engagement-metrics/se.analytics.dashboard.component";
import { SeEngagementConfigComponent } from "../modules/student-engagement/engagement-metrics/se.engagement.config.component";
import { SeBadgeConfigComponent } from "../modules/student-engagement/engagement-metrics/se.badge.config.component";
import { SeLowActivityComponent } from "../modules/student-engagement/engagement-metrics/se.low.activity.component";
import { SeBadgeNotificationComponent } from "../modules/student-engagement/engagement-metrics/se.badge.notification.component";
import { S25AcademicGridsPageComponent } from "../modules/s25-academic-grid/s25.academic.grids.page.component";
import { IdCardsConfigComponent } from "../modules/student-engagement/id-cards/id.cards.config.component";
import { WorkflowSubmissionAuditPageComponent } from "../modules/workflow/actions/workflow.submission.audit.page.component";
import { SeEmailPageComponent } from "../modules/student-engagement/se.email.page.component";
import { SeEmailComposeComponent } from "../modules/student-engagement/email/se.email.compose.component";
import { SeEmailSentComponent } from "../modules/student-engagement/email/se.email.sent.component";
import { SeEmailSentDetailComponent } from "../modules/student-engagement/email/se.email.sent.detail.component";
import { SeEmailSentFilterComponent } from "../modules/student-engagement/email/se.email.sent.filter.component";
import { S25EventEmailHistoryComponent } from "../modules/s25-event-email-history/s25.event.email.history.component";
import { SeLayoutsConfigComponent } from "../modules/student-engagement/layouts/se.layouts.config.component";
import { AcademicComingSoonComponent } from "../modules/academic-curriculum/academic.coming.soon.component";
import { AcademicHomeComponent } from "../modules/academic-curriculum/academic.home.component";
import { AcademicSidebarComponent } from "../modules/academic-curriculum/academic.sidebar.component";
import { AcademicTopBannerComponent } from "../modules/academic-curriculum/academic.top.banner.component";
import { TrailAdminPageComponent } from "../modules/student-engagement/trails/trail.admin.page.component";
import { TrailAdminProgressComponent } from "../modules/student-engagement/trails/trail.admin.progress.component";
import { TrailCatalogComponent } from "../modules/student-engagement/trails/trail.catalog.component";
import { TrailCohortAdminComponent } from "../modules/student-engagement/trails/trail.cohort.admin.component";
import { TrailCurriculumCreateComponent } from "../modules/student-engagement/trails/trail.curriculum.create.component";
import { TrailFocusAreaAdminComponent } from "../modules/student-engagement/trails/trail.focus.area.admin.component";
import { TrailCurriculumEditorComponent } from "../modules/student-engagement/trails/trail.curriculum.editor.component";
import { TrailExperienceAdminComponent } from "../modules/student-engagement/trails/trail.experience.admin.component";
import { TrailExperienceListComponent } from "../modules/student-engagement/trails/trail.experience.list.component";
import { TrailExperienceSubmitComponent } from "../modules/student-engagement/trails/trail.experience.submit.component";
import { TrailProgressComponent } from "../modules/student-engagement/trails/trail.progress.component";
import { TrailRefreshButtonComponent } from "../modules/student-engagement/trails/trail.refresh.button.component";
import { TrailRequirementTreeComponent } from "../modules/student-engagement/trails/trail.requirement.tree.component";
import { TrailCelebrationComponent } from "../modules/student-engagement/trails/trail.celebration.component";
import { TrailStudentPageComponent } from "../modules/student-engagement/trails/trail.student.page.component";
import { S25ConfigureThemeComponent } from "../standalone/configure-theme/s25.configure.theme.component";
import { HomeDashAvailableSpaceFindAvailabilityComponent } from "../standalone/home/dash/panel/available-space/find-availability/home.dash.available.space.find.availability.component";
import { HomeDashAvailableSpaceFindLocationComponent } from "../standalone/home/dash/panel/available-space/find-location/home.dash.available.space.find.location.component";
import { HomeDashPanelAvailableSpaceComponent } from "../standalone/home/dash/panel/available-space/home.dash.panel.available.space.component";
import { HomeDashPanelComponent } from "../standalone/home/dash/panel/home.dash.panel.component";
import { HomeDashPanelExpressSchedulingComponent } from "../standalone/home/dash/panel/express-scheduling/home.dash.panel.express.scheduling.component";
import { HomeDashPanelListComponent } from "../standalone/home/dash/panel/list/home.dash.panel.list.component";
import { HomeDashPanelListItemComponent } from "../standalone/home/dash/panel/list/home.dash.panel.list.item.component";
import { HomeDashPanelQuickSearchComponent } from "../standalone/home/dash/panel/quick-search/home.dash.panel.quick.search.component";
import { HomeDashSectionComponent } from "../standalone/home/dash/section/home.dash.section.component";
import { HomeDashComponent } from "../standalone/home/dash/home.dash.component";
import { ItemColorCustomComponent } from "../standalone/item-color-mapping/item.color.custom.component";
import { ItemColorMappingComponent } from "../standalone/item-color-mapping/item.color.mapping.component";
import { EventFormObjectAvailabilityComponent } from "../standalone/event-form/object-availability/event.form.object.availability.component";
import { EventFormObjectChooser } from "../standalone/event-form/object-details/object.chooser.component";
import { EventFormObjectOccurrencesTableComponent } from "../standalone/event-form/object-occurrences/event.form.object.occurrences.table.component";
import { SeGuestRegistrationFormComponent } from "../modules/student-engagement/attendance/se.guest.registration.form.component";
import { SeGuestRegistrationConfirmComponent } from "../modules/student-engagement/attendance/se.guest.registration.confirm.component";
import { S25EventQuotaAdminComponent } from "../modules/system-settings/event-form-settings/s25.event.quota.admin.component";
import { S25EventPricingComponent } from "../modules/s25-pricing/reference-set-pricing/s25.event.pricing.component";
import { S25InvoiceViewComponent } from "../modules/s25-pricing/reference-set-pricing/invoice/s25.invoice.component";
import { S25InvoiceControlPanelComponent } from "../modules/s25-pricing/reference-set-pricing/invoice/invoice-control-panel/s25.invoice.control.panel.component";
import { S25PricingRateGroupDropdown } from "../modules/s25-pricing/reference-set-pricing/pricing-rate-group-dropdown/s25.pricing.rate.group.dropdown";
import { S25PricingSidebarComponent } from "../modules/s25-pricing/reference-set-pricing/sidebar/s25.pricing.sidebar.component";
import { S25PricingPrintInvoiceComponent } from "../modules/s25-pricing/reference-set-pricing/print-invoice/s25.pricing.print.invoice.component";
import { S25IncludedOccurrencesComponent } from "../modules/s25-pricing/reference-set-pricing/included-occurrences/s25.included.occurrences.component";
import { S25PricingAdjustmentCellComponent } from "../modules/s25-pricing/reference-set-pricing/invoice/adjustment-cell/s25.pricing.adjustment.cell.component";
import { S25PricingTabComponent } from "../modules/s25-pricing/s25.pricing.tab.component";
import { S25OrgInvoicesComponent } from "../modules/s25-pricing/org-pricing/s25.org.invoices.component";

declare global {
    interface Window {
        angBridge: any;
        angular: any;
        unitTesting: boolean;
    }
}

@NgModule({
    declarations: [AppComponent],
    schemas: [],
    imports: [
        BrowserModule,
        S25HelpModule,
        S25ToggleButtonModule,
        S25EditableBooleanModule,
        S25MultiQueryModule,
        S25AddressModule,
        ModalModule,
        S25NumWeeksChooserModule,
        S25ItemModule,
        S25ItemComponent,
        S25ItemUnwrapperComponent,
        PopoverModule,
        S25ImageModule,
        EmailDefaultsComponent,
        SystemDefaultsComponent,
        UserDefaultsComponent,
        EventSettingsComponent,
        LocationSettingsComponent,
        ResourceSettingsComponent,
        OrganizationSettingsComponent,
        TaskSettingsComponent,
        S25RichTextEditorModule,
        S25BreakpointModule,
        S25DatetimePrefsComponent,
        S25TimepickerModule,
        S25DatepickerModule,
        S25DropdownPaginatedModule,
        S25LoadingInlineModule,
        S25AiSearchChatbotComponent,
        S25SimpleCollapseModule,
        S25LynxModule,
        S25PricingModule,
        S25NotificationPolicyModule,
        S25LynxModule,
        S25ImageUploadModule,
        S25EditableModule,
        BpeModule,
        EventStateDefaultComponent,
        S25MultiselectModule,
        S25BulkEditModule,
        S25SecurityLinkModule,
        S25PipesModule,
        S25DeleteModule,
        PublisherSettingsComponent,
        S25ContactInfoModule,
        S25EventTypeHierarchyModule,
        EventFormSettingsModule,
        S25ImageModule,
        S25ImageSelectorModule,
        S25ImageFormModule,
        S25ChangePasswordModule,
        S25MasqueradeModule,
        S25ChangePasswordModule,
        S25DayChooserModule,
        S25CancelRequestModule,
        S25PreferenceToggleComponent,
        S25RegisterButtonModule,
        S25LocationAlwaysShareModule,
        S25OptModule,
        S25EventStatesModule,
        S25ConflictsModule,
        S25OfficeHoursSliderModule,
        S25TaskModule,
        S25ConstraintsModule,
        S25ObjectModule,
        S25DndSortableModule,
        S25TableModule,
        S25RuleTreeModule,
        S25EventFormConfigsModule,
        S25EmailTemplateModule,
        S25InfoMessageModule,
        S25MasterDefinitionsModule,
        S25EmailScenarioModule,
        S25ChartsModule,
        S25RadioModule,
        S25CustomAttributeModule,
        S25TriggerRerenderModule,
        S25WorkflowModule,
        S25DocumentManagementModule,
        S25TaskViewModule,
        S25EmbeddingModule,
        S25ModalModule,
        S25IconComponent,
        S25HomeSearchComponent,
        SaveSearchModalComponent,
        OrganizationExportModalComponent,
        S25ModalShareSearchComponent,
        S25CaptureEventModule,
        S25PublisherModule,
        S25PillModule,
        S25BoardModule,
        S25ProgressBarModule,
        S25OptimizerEffectiveDatingModule,
        S25ShiftSelectableModule,
        S25BlackoutManagementModule,
        S25SpecialDateManagementModule,
        S25AvailabilityModule,
        S25BuildingModule,
        S25StandardScheduleModule,
        S25EventDetailsModule,
        ContextMenuModule,
        S25DurationModule,
        S25EventOccurrencesComponent,
        S25StripeSettingsModule,
        S25SevenPointSolutionsSettingsModule,
        S25DocusignSettingsComponent,
        S25ObjectAuditListModule,
        S25ScheduledEmailsModule,
        S25MeetingPatternGridModule,
        S25ScrollMinimapComponent,
        S25VirtualGridModule,
        S25ButtonComponent,
        S25ErrorsComponent,
        UserActivityModule,
        S25ButtonComponent,
        S25ReportFileTypeComponent,
        S25ObjectSearchModule,
        StudentHousingModule,
        S25LinkParserComponent,
        S25ServicePortalModule,
        S25AvailWeeklyComponent,
        S25AvailLegendComponent,
        S25InlineDatepickerComponent,
        S25MultiAvailWeeklyComponent,
        S25EmbeddedMultiAvailWeeklyComponent,
        SisImportExportSettingsComponent,
        S25ChatbotComponent,
        S25ExpressSchedulingChatbotComponent,
        S25ExpressSchedulingComponent,
        EventFormModule,
        S25CopyVariableComponent,
        S25TabGroupComponent,
        EvOccMappingComponent,
        S25ClickOutsideDirective,
        AdmitModule,
        S25SidebarComponent,
        S25FadingSuccessMsgComponent,
        CalendaringModule,
        S25NotificationsModule,
        S25ObjectDetailsModule,
        S25NavBarComponent,
        ImportModule,
        S25DisplayFileComponent,
        GoogleMapsModule,
        S25ItemNameComponent,
        SeHomeComponent,
        SeLoginComponent,
        PasswordResetComponent,
        S25UserProfileComponent,
        SeOrgHomeComponent,
        MyWorkflowPageComponent,
        WorkflowProcessesPageComponent,
        WorkflowProcessEditorComponent,
        WorkflowAddFormActionsModalComponent,
        WorkflowInitRulesComponent,
        WorkflowActionHistoryPageComponent,
        BudgetAdminComponent,
        BudgetCategoryManagementComponent,
        FiscalYearManagementComponent,
        BudgetAdminRequestsComponent,
        BudgetReportsComponent,
        S25ChatComponent,
        S25ChatPageComponent,
        S25ProductFeedbackComponent,
        SeConfigComponent,
        SeConfigInitPageComponent,
        SeConfigAppearancePageComponent,
        SeConfigAuthenticationPageComponent,
        SeConfigAlertsPageComponent,
        SeEmailTemplateSettingsPageComponent,
        Connect25EmailTemplateAdminComponent,
        SeConfigImportPageComponent,
        SeOrgStatusAdminComponent,
        SeSecurityComponent,
        SeObjectSearchComponent,
        SeUserSearchComponent,
        SeServiceHoursComponent,
        MoreActionsCollapseComponent,
        DataIngestionModule,
        CalendarWithOptions,
        ListWithOptions,
        ApprovalGroupsComponent,
        SeEventDetailsViewComponent,
        ManageTaskComponent,
        SeSidebarComponent,
        SeBreadcrumbComponent,
        Connect25TopBannerComponent,
        Connect25SystemAlertsComponent,
        SeSystemAlertsAdminComponent,
        SsoMappingsAdminComponent,
        ElectionBallotComponent,
        ElectionNominationsPageComponent,
        ElectionAdminPageComponent,
        ElectionManualCandidatePageComponent,
        ElectionResultsPageComponent,
        S25ReportOutputFileNameComponent,
        S25EventSummaryComponent,
        EventSurveyComponent,
        FormSubmissionComponent,
        SeEventCheckinDashComponent,
        SeCheckInComponent,
        WaitlistComponent,
        SeMyActivityComponent,
        SeEngagementDashboardComponent,
        SeAnalyticsDashboardComponent,
        SeEngagementConfigComponent,
        SeBadgeConfigComponent,
        SeLowActivityComponent,
        SeBadgeNotificationComponent,
        S25AcademicGridsPageComponent,
        AcademicComingSoonComponent,
        AcademicHomeComponent,
        AcademicSidebarComponent,
        AcademicTopBannerComponent,
        IdCardsConfigComponent,
        WorkflowSubmissionAuditPageComponent,
        RecentlyViewed,
        SeEmailPageComponent,
        SeEmailComposeComponent,
        SeEmailSentComponent,
        SeEmailSentDetailComponent,
        SeEmailSentFilterComponent,
        S25EventEmailHistoryComponent,
        SeLayoutsConfigComponent,
        TrailAdminPageComponent,
        TrailAdminProgressComponent,
        TrailCatalogComponent,
        TrailCelebrationComponent,
        TrailCohortAdminComponent,
        TrailCurriculumCreateComponent,
        TrailFocusAreaAdminComponent,
        TrailCurriculumEditorComponent,
        TrailExperienceAdminComponent,
        TrailExperienceListComponent,
        TrailExperienceSubmitComponent,
        TrailProgressComponent,
        TrailRefreshButtonComponent,
        TrailRequirementTreeComponent,
        TrailStudentPageComponent,
        S25ConfigureThemeComponent,
        HomeDashPanelComponent,
        HomeDashPanelExpressSchedulingComponent,
        HomeDashPanelQuickSearchComponent,
        HomeDashAvailableSpaceFindAvailabilityComponent,
        HomeDashAvailableSpaceFindLocationComponent,
        HomeDashPanelAvailableSpaceComponent,
        HomeDashPanelListComponent,
        HomeDashPanelListItemComponent,
        HomeDashSectionComponent,
        HomeDashComponent,
        ItemColorCustomComponent,
        ItemColorMappingComponent,
        EventFormObjectAvailabilityComponent,
        EventFormObjectChooser,
        EventFormObjectOccurrencesTableComponent,
        EventFormProfileDropdownComponent,
        MultiselectItemComponent,
        SeGuestRegistrationFormComponent,
        SeGuestRegistrationConfirmComponent,
        S25EventQuotaAdminComponent,
        S25EventPricingComponent,
        S25InvoiceViewComponent,
        S25InvoiceControlPanelComponent,
        S25PricingRateGroupDropdown,
        S25PricingSidebarComponent,
        S25PricingPrintInvoiceComponent,
        S25IncludedOccurrencesComponent,
        S25PricingAdjustmentCellComponent,
        S25PricingTabComponent,
        S25OrgInvoicesComponent,
    ],
    providers: [
        provideHttpClient(withXhr()),
        {
            provide: ErrorHandler,
            useValue: window.unitTesting
                ? null
                : Sentry.createErrorHandler({
                      showDialog: false,
                  }),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [Sentry.TraceService],
            multi: true,
        },
        AppComponent,
    ],
})
export class AppModule implements DoBootstrap {
    /**
     * Components that are only ever instantiated through the AngularJS bridge
     * (`<s25-angular-wrapper>` -> `CompileService.compileElement` -> `TYPE_MAP`), and are
     * never referenced by an Angular template, route, or `bootstrap`. Angular keeps no
     * runtime reference to them, and an NgModule's import-scope metadata is stripped from
     * production AOT builds, so the only thing left pointing at them is their
     * `@TypeManagerDecorator` self-registration. Angular emits that custom decorator as a
     * pure-annotated `__decorate(...)` call, which lets terser tree-shake the whole class
     * (and its `TYPE_MAP` registration) out of prod builds, leaving the `<s25-ng-...>`
     * element empty with no error. Referencing the classes here (see constructor) forces
     * retention.
     *
     * Add any new bridge-only page component (its selector appears only in
     * framework-components.js) to this list.
     */
    private static readonly bridgeOnlyComponents = [
        SeGuestRegistrationConfirmComponent,
        SeGuestRegistrationFormComponent,
        SeCheckInComponent,
        EventSurveyComponent,
        FormSubmissionComponent,
        SeConfigComponent,
        SeEmailTemplateSettingsPageComponent,
        Connect25EmailTemplateAdminComponent,
        S25EventQuotaAdminComponent,
        EventFormProfileDropdownComponent,
        AcademicComingSoonComponent,
        AcademicHomeComponent,
        AcademicSidebarComponent,
        AcademicTopBannerComponent,
        TrailStudentPageComponent,
        TrailAdminPageComponent,
        TrailAdminProgressComponent,
        TrailExperienceAdminComponent,
        EventFormObjectChooser,
        EventFormObjectOccurrencesTableComponent,
        MultiselectItemComponent,
        S25ChatPageComponent,
        S25ProductFeedbackComponent,
        BudgetReportsComponent,
        S25OrgInvoicesComponent,
        S25SpecialDateBulkUploadComponent,
        EmailDefaultsComponent,
        SystemDefaultsComponent,
        UserDefaultsComponent,
        EventSettingsComponent,
        LocationSettingsComponent,
        ResourceSettingsComponent,
        OrganizationSettingsComponent,
        TaskSettingsComponent,
        S25DatetimePrefsComponent,
        EventStateDefaultComponent,
        PublisherSettingsComponent,
        S25NavBarComponent,
        S25DocusignSettingsComponent,
    ];

    readonly seSecurityStore = inject(SeSecurityStore);
    constructor(
        private injector: Injector,
        private zone: NgZone,
        private compileService: CompileService,
    ) {
        ServiceLoader.injector = injector;

        // Retain bridge-only components so their @TypeManagerDecorator registrations survive
        // production tree-shaking. The window assignment is an observable side-effect terser
        // cannot drop, which keeps the referenced classes (and their TYPE_MAP entries).
        window.angBridge.bridgeOnlyComponents = AppModule.bridgeOnlyComponents;
        window.angBridge.CompileService = this.compileService;
        window.angBridge.zone = this.zone;
        window.angBridge.LoginStore = inject(LoginStore);
        window.angBridge.FlsStore = inject(FlsStore);
        window.angBridge.SeSecurityStore = this.seSecurityStore;
        window.angBridge.RecentlyViewedStore = inject(RecentlyViewedStore);
        //Note: maintenancePage.js overrides XMLHttpRequest.prototype.open to always send async, which is required for this to work
        window.angBridge.HttpClient = new HttpClient(
            new HttpXhrBackend({
                build: () => {
                    return new XMLHttpRequest();
                },
            }),
        );
    }

    // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method
    ngDoBootstrap() {
        // this is a very uncommon angular lifecycle event, so we're explicitly
        // marking it here to make sure people know about it.
        // delete this comment if you add some initialization logic here.
    }
}
